import React from "react";
import { Quote, Star, GraduationCap, BookOpen, Users, Award } from "lucide-react";

const DirectorMessage = () => {
  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4 md:p-6 relative overflow-hidden">
      
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
        <div className="absolute top-40 left-1/2 w-80 h-80 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>
      </div>

      {/* Main Container */}
      <div className="max-w-5xl w-full relative z-10">
        
        {/* Decorative Top Bar */}
        <div className="flex justify-center mb-3">
          <div className="h-0.5 w-24 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-full"></div>
        </div>
        
        {/* Main Card */}
        <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden border border-white/20 hover:shadow-2xl transition-all duration-500">
          
          {/* Gradient Border */}
          <div className="h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500"></div>
          
          <div className="p-6 md:p-8 lg:p-10">
            
            {/* Header Section */}
            <div className="flex flex-col lg:flex-row items-center lg:items-start gap-6 mb-8">
              
              {/* Circular Image Container */}
              <div className="relative flex-shrink-0 group">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full blur-md opacity-60 group-hover:opacity-80 transition-opacity duration-300"></div>
                <div className="relative rounded-full p-1 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 shadow-xl group-hover:shadow-2xl transition-all duration-300">
                  <img
                    src="/director.png"
                    alt="Director Sandeep Singh"
                    className="w-32 h-32 md:w-40 md:h-40 lg:w-48 lg:h-48 object-cover rounded-full shadow-md group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute -bottom-1 -right-1 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full p-1.5 shadow-lg">
                    <Award className="w-4 h-4 text-white" />
                  </div>
                </div>
              </div>

              {/* Text Info */}
              <div className="text-center lg:text-left flex-1">
                <div className="mb-3">
                  <span className="inline-block px-3 py-1 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-full text-indigo-700 text-xs font-semibold">
                    Since 1998
                  </span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
                  Mr. Sandeep Singh
                </h2>
                <div className="inline-block px-4 py-1 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full mb-3 shadow-md">
                  <p className="font-semibold text-white text-sm">Director & Founder</p>
                </div>
                <div className="space-y-1">
                  <p className="text-gray-700 text-sm flex items-center justify-center lg:justify-start gap-2">
                    <GraduationCap className="w-4 h-4 text-indigo-500" />
                    <span className="font-medium">BNS Mahila Maha Vidyalaya</span>
                  </p>
                  <p className="text-gray-500 text-xs flex items-center justify-center lg:justify-start gap-2">
                    <span>📍</span>
                    Akhari Bypass, Varanasi
                  </p>
                </div>
              </div>
            </div>

            {/* Message Section */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Quote className="w-5 h-5 text-indigo-500" />
                <h3 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                  Director's Message
                </h3>
              </div>
            </div>

            {/* Message Content - Smaller Text */}
            <div className="text-gray-700 text-justify leading-relaxed space-y-4 text-sm md:text-base">
              <div className="relative pl-4 border-l-3 border-indigo-400 bg-gradient-to-r from-indigo-50/50 to-transparent p-3 rounded-r-lg">
                <p className="first-letter:text-3xl first-letter:font-bold first-letter:text-indigo-600 first-letter:mr-2 first-letter:float-left">
                  Education is a fundamental necessity of human life, just like food,
                  clothing, and shelter. Without education, a person remains incomplete.
                  It is a continuous process that guides an individual from birth to
                  the end of life.
                </p>
              </div>

              <p>
                After independence, both the government and society have made
                significant efforts to improve the education system. Our institution
                is committed to providing quality education so that students can
                understand their responsibilities towards society and the nation.
              </p>

              {/* Institutions List - Compact */}
              <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-xl p-4 my-5">
                <div className="flex items-center gap-2 mb-3">
                  <BookOpen className="w-4 h-4 text-indigo-600" />
                  <p className="font-semibold text-gray-800 text-sm">Our Institutions:</p>
                </div>
                <ul className="space-y-2">
                  {[
                    "B.N.S. English School, Varanasi (Recognized up to Class 12)",
                    "B.N.S. School, Chandpur Railway Station, Varanasi",
                    "BNS Mahila Maha Vidyalaya, Varanasi"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-2 group">
                      <Star className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="text-gray-600 text-sm italic bg-white/50 p-3 rounded-lg">
                The institution is continuously working for the benefit of students
                from all sections of society and is progressing steadily.
              </p>

              {/* Gratitude Section */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="relative">
                  <Quote className="w-5 h-5 text-indigo-300 absolute -top-3 -left-1 opacity-50" />
                  <p className="italic text-gray-700 text-sm leading-relaxed pl-5">
                    "I express my sincere gratitude to the parents and teachers whose
                    support and cooperation have made the growth of this institution possible."
                  </p>
                </div>
                
                {/* Signature */}
                <div className="mt-5 text-right">
                  <div className="inline-block text-left">
                    <div className="mb-1">
                      <svg className="w-32 h-8" viewBox="0 0 200 50">
                        <path d="M10,30 Q30,15 50,25 T90,20 T130,30 T170,25" 
                              stroke="url(#gradient)" strokeWidth="1.5" fill="none" 
                              className="opacity-60"/>
                        <defs>
                          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#4f46e5" />
                            <stop offset="100%" stopColor="#9333ea" />
                          </linearGradient>
                        </defs>
                      </svg>
                    </div>
                    <div>
                      <p className="text-gray-800 text-sm font-semibold">— Mr. Sandeep Singh</p>
                      <p className="text-gray-500 text-xs">Director, B.N.S. Group of Institutions</p>
                      <div className="flex gap-1.5 justify-end mt-1">
                        <Users className="w-3 h-3 text-indigo-400" />
                        <span className="text-xs text-gray-400">Empowering minds since 1998</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer Decoration */}
        <div className="flex justify-center gap-2 mt-4">
          {['bg-indigo-400', 'bg-purple-400', 'bg-pink-400'].map((color, index) => (
            <div 
              key={index} 
              className={`h-1 w-8 ${color} rounded-full opacity-60`}
            ></div>
          ))}
        </div>
      </div>

      <style jsx>{`
        @keyframes blob {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        
        .animate-blob {
          animation: blob 7s infinite;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        
        .border-l-3 {
          border-left-width: 3px;
        }
      `}</style>
    </div>
  );
};

export default DirectorMessage;